import { useTranslation } from 'react-i18next';
import React, { useEffect, useRef } from 'react';
import { APIProvider } from '@vis.gl/react-google-maps';
import _ from 'lodash';
import useStateRef from 'react-usestateref';
import { useStorage } from '../../hooks/use-storage';
import { useSwal } from '../../hooks/use-swal';
import { AddressMapModalBody } from '../../components/location/address-map-modal-body';
import { useErrorHandler } from '../../hooks/use-error-handler';
import { maxDistancePermittedBeetwenMapPoints } from '../../utils/manager/map-manager';
import { useAnalytics } from '../../utils/setup/analytics-setup';

function useAddressMapModal() {
  const storage = useStorage();
  const { config, menuInfo } = storage;
  const { swal } = useSwal({ storage });
  const { handleErrorOnModal } = useErrorHandler();
  const { analyticsPageView } = useAnalytics();
  const [message, setMessage, messageRef] = useStateRef();
  const { t } = useTranslation();

  const focusedRef = useRef(false);

  const centerRef = useRef();

  const didOpen = () => {
    analyticsPageView('Address Map');
    focusedRef.current = true;
  };

  const didDestroy = async () => {
    focusedRef.current = false;
  };

  useEffect(() => {
    if (!focusedRef.current) return;

    if (_.isEmpty(message)) {
      swal.resetValidationMessage();
    } else {
      swal.showValidationMessage(message);
    }
  }, [message, focusedRef]);

  async function clear() {
    centerRef.current = null;
  }

  async function show({
    placeDetails,
    baseLocation,
    noStreetNumber,
    complement,
    prepareOnPreConfirm,
  }) {
    let distance;

    const result = await swal.fire({
      html: (
        <APIProvider
          apiKey={config?.maps?.browser?.api_key}
          language={menuInfo?.i18n?.default_language?.tag}
        >
          <AddressMapModalBody
            config={config}
            placeDetails={placeDetails}
            baseLocation={baseLocation}
            noStreetNumber={noStreetNumber}
            center={centerRef.current}
            onCenterChanged={(value) => {
              centerRef.current = value;
              setMessage(null);
            }}
            onDistanceChanged={(value) => {
              distance = value;
            }}
            complement={complement}
          />
        </APIProvider>
      ),
      didOpen,
      didDestroy,
      showConfirmButton: true,
      showDenyButton: false,
      showCancelButton: true,
      confirmButtonText: t('save'),
      cancelButtonText: t('back'),
      showLoaderOnConfirm: true,
      preConfirm: () => preConfirm({ distance, prepareOnPreConfirm }),
    });

    if (result.isConfirmed) {
      result.center = centerRef.current;
    }

    result.wasMapBackButtonClicked = result.isDismissed;
    result.wasMapMarkerInvalid = !_.isEmpty(messageRef.current);

    return result;
  }

  async function preConfirm({ distance, prepareOnPreConfirm }) {
    try {
      if (distance > maxDistancePermittedBeetwenMapPoints) {
        setMessage(t('address-map-modal.distance-exceeded'));

        return false;
      }

      await prepareOnPreConfirm?.call(this, centerRef.current);
    } catch (error) {
      handleErrorOnModal(error);
      return false;
    }

    return true;
  }

  return {
    clearAddressMapModal: clear,
    showAddressMapModal: show,
  };
}

export { useAddressMapModal };
